import React, {useCallback, useEffect, useRef, useState, useTransition} from 'react';
import {
    checkNullArr,
    checkNullJson,
    checkNullStr,
    getArrIndexValue,
    getArrLen,
    getJsonValue,
    getHighlightedMatchedText,
    isArr,
    isBoolTrue,
    isJsonValueTrue,
    multSize,
    printError,
    printLog,
    scrollToElementInScroller,
    subtractSize
} from "../utils/ReactUtils";
import {Avatar, CircleLoading, Flexbox, Label, NullMsgLabel} from "../index";
import MenuItem from "@mui/material/MenuItem";
import Button from "../action/Button";
import {Menu, Tooltip} from "@mui/material";
import {CssVariant, RippleVarVariant} from "../config/FusionConfig";

/**
 * AutoCompleteView component for selecting multiple items from a list and as tags.
 * It provides autocomplete functionality for selecting items from a list of options.
 * It displays a list of options in a dropdown menu, allows the user to search for options,
 * and select multiple items. The component includes customizable styling options and supports
 * various features such as displaying images, showing counts, and allowing tag removal.
 *
 * @param {string} wrapperClassName - CSS class name for the wrapper div.
 * @param {string} nameClassName - CSS class name for the name.
 * @param {string} imageClassName - CSS class name for the image.
 * @param {string} labelClassName - CSS class name for the label.
 * @param {string} countClassName - CSS class name for the count.
 * @param {string} tagClassName - CSS class name for the tag.
 * @param {string} removeClassName - CSS class name for the remove button.
 * @param {string} dropdownItemClassName - CSS class name for the dropdown item.
 * @param {string} dropdownMenuClassName - CSS class name for the dropdown menu.
 * @param {string} searchClassName - CSS class name for the search input.
 * @param {string} tagBoxClassName - CSS class name for the tag box.
 * @param {string} refreshClassName - CSS class name for the refresh button.
 * @param {string} selectAllClassName - CSS class name for select-all checkbox.
 * @param {string} msgLabelColor - Color for the message label.
 * @param {string} nameColor - Color for the name.
 * @param {string} searchColor - Color for the search input text.
 * @param {string} dropdownItemHoverBg - Background color for dropdown item on hover.
 * @param {string} dropdownItemHoverColor - Text color for dropdown item on hover.
 * @param {string} dropdownItemSelectedBg - Background color for selected dropdown item.
 * @param {string} dropdownItemSelectedColor - Text color for selected dropdown item.
 * @param {string} tagBoxBorderColor - Border color for the tag box.
 * @param {string} labelColor - Color for the label.
 * @param {string} countColor - Color for the count.
 * @param {string} tagBg - Background color for the tag.
 * @param {string} listMsg - Message to be displayed in the list.
 * @param {number} dropdownHeight - Height of the dropdown menu.
 * @param {string} searchPlaceholder - Placeholder text for the search input.
 * @param {string} label - Label for the component.
 * @param {boolean} required - Whether the component is required.
 * @param {boolean} showLoading - Whether to show loading indicator.
 * @param {boolean} showCount - Whether to show the count.
 * @param {boolean} showRefresh - Whether to show the refresh-list button.
 * @param {boolean} disableRefresh - Disable refresh-list button.
 * @param {boolean} refreshLoading - Show refresh-list button as loading.
 * @param {string} itemNameKey - Key for item name in the options array.
 * @param {string} itemImageKey - Key for item image in the options array.
 * @param {string} itemHoverAltNameKey - Key for tag-item hover to show tooltip.
 * @param {string} itemRemovableKey - Key for tag-item's removable-property to make the tag fixed or removable.
 * @param {string} showAltNameOnHover - Whether to show tooltip on tag hover or not.
 * @param {string} show - Key for item image in the options array.
 * @param {string} imageSize - Size of the image.
 * @param {boolean} showNameInitials - Whether to show name initials if image is not available.
 * @param {boolean} showSelectedItems - Whether to show selected items in options dropdown or not.
 * @param {boolean} showOptionsAfterSelection - Whether to show options dropdown after item-selection or not.
 * @param {boolean} showSelectAll - Show select-all checkbox. Default is true.
 * @param {boolean} synced - Whether to keep component data and src-option-list data in sync.
 * @param {boolean} removableTag - Whether tags are removable.
 * @param {string} removeColor - Color for the remove-tag button.
 * @param {string} removeSize - Size of the remove-tag button.
 * @param {string} controlSize - Size of the refresh button.
 * @param {string} tagBoxHeight - Height of the tag-list container.
 * @param {string} tagBoxMinHeight - Min-height of the tag-list container.
 * @param {array} options - Array of options. Format: [ { name: 'Name 1', 'image': 'image-1.png' }, ...rest ]
 * @param {function} onSelected - Function called with updated list and selected list.
 * @param {object} wrapperStyle - Inline style for the wrapper div.
 * @param {object} nameStyle - Inline style for the name.
 * @param {object} imageStyle - Inline style for the image.
 * @param {object} tagStyle - Inline style for the tag.
 * @param {object} tagHoverStyle - Inline style for the tag on hover.
 * @param {object} tagPressStyle - Inline style for the tag when pressed.
 * @param {object} dropdownMenuStyle - Inline style for the dropdown menu.
 * @param {object} refreshStyle - Inline style for the refresh button.
 * @param {object} dropdownStyle - Inline style for the dropdown.
 * @param {object} dropdownItemStyle - Inline style for the dropdown item.
 * @param {object} dropdownItemSelectedStyle - Inline style for the selected dropdown item.
 * @param {object} dropdownItemHoverStyle - Inline style for the dropdown item on hover.
 * @param {object} dropdownItemPressStyle - Inline style for the dropdown item when pressed.
 * @param {object} removeStyle - Inline style for the remove-tag button.
 * @param {object} labelStyle - Inline style for the label.
 * @param {object} countStyle - Inline style for the count.
 * @param {object} searchStyle - Inline style for the search input.
 * @param {object} selectAllStyle - Inline style for the select-all checkbox.
 *
 * @example:
 * const OPTIONS_DATA=[
 *     { "name": "John Doe", "image": "https://randomuser.me/api/portraits/men/1.jpg" },
 *     { "name": "Jane Smith", "image": "https://randomuser.me/api/portraits/women/2.jpg" },
 *     { "name": "Bob Johnson", "image": "https://randomuser.me/api/portraits/men/3.jpg" },
 *     { "name": "Alice Williams", "image": "https://randomuser.me/api/portraits/women/4.jpg" },
 *     { "name": "Charlie Brown", "image": "https://randomuser.me/api/portraits/men/5.jpg" },
 *     { "name": "Emma Jones", "image": "https://randomuser.me/api/portraits/women/6.jpg" },
 *     { "name": "Michael Wilson", "image": "https://randomuser.me/api/portraits/men/7.jpg" },
 *     { "name": "Olivia Taylor", "image": "https://randomuser.me/api/portraits/women/8.jpg" },
 *     { "name": "William Martinez", "image": "https://randomuser.me/api/portraits/men/9.jpg" },
 *     { "name": "Sophia Anderson", "image": "https://randomuser.me/api/portraits/women/10.jpg" },
 *     { "name": "Matthew Thomas", "image": "https://randomuser.me/api/portraits/men/11.jpg"}
 * ];
 * const [optionList, setOptionList] = useState(OPTIONS_DATA);
 *
 * <AutoCompleteView
 *         wrapperClassName={''}
 *         dropdownItemClassName={''}
 *         nameClassName={''}
 *         imageClassName={''}
 *         labelClassName={''}
 *         countClassName={''}
 *         removeClassName={''}
 *         searchClassName={''}
 *         tagBoxClassName={''}
 *         refreshClassName={''}
 *         tagClassName={''}
 *         dropdownMenuClassName={''}
 *
 *         msgLabelColor={'gray'}
 *         nameColor={'black'}
 *         searchColor={'black'}
 *         dropdownItemHoverBg={'lightgray'}
 *         dropdownItemHoverColor={'black'}
 *         dropdownItemSelectedBg={'darkgray'}
 *         dropdownItemSelectedColor={'gray'}
 *         tagBoxBorderColor={'lightgray'}
 *         labelColor={''}
 *         countColor={'darkgray'}
 *         tagBg={'#e5e5e5'}
 *         listMsg={""}
 *         dropdownHeight={300}
 *         searchPlaceholder={'Search tags...'}
 *         label={"Recipients"}
 *         required={true}
 *         showLoading={false}
 *         showCount={true}
 *         showRefresh={true}
 *         refreshLoading={false}
 *         disableRefresh={false}
 *         controlSize={'18px'}
 *
 *         options={optionList}
 *         itemNameKey={'name'}
 *         itemImageKey={'image'}
 *         itemHoverAltNameKey={'name'}
 *         itemRemovableKey={'removable'}
 *         showAltNameOnHover={true}
 *
 *         imageSize={'22px'}
 *         showNameInitials={true}
 *         showOptionsAfterSelection={false}
 *         showSelectedItems={false}
 *         showSelectAll={true}
 *         synced={false}
 *         removableTag={true}
 *         removeColor={'black'}
 *         tagBoxMinHeight={''}
 *         tagBoxHeight={'35vh'}
 *         listHeight={'50vh'}
 *
 *         wrapperStyle={{}}
 *         nameStyle={{}}
 *         imageStyle={{}}
 *         tagStyle={{}}
 *         tagHoverStyle={{}}
 *         tagPressStyle={{}}
 *         refreshStyle={{}}
 *         dropdownMenuStyle={{}}
 *         dropdownStyle={{}}
 *         dropdownItemStyle={{}}
 *         dropdownItemSelectedStyle={{}}
 *         dropdownItemHoverStyle={{}}
 *         dropdownItemPressStyle={{}}
 *         removeStyle={{}}
 *         labelStyle={{}}
 *         countStyle={{}}
 *         searchStyle={{}}
 *         selectAllStyle={{}}
 *
 *         onSelected={(updatedList, selectedList) => {
 *             setOptionsList(updatedList);
 *             console.log('optionList.onSelected.updatedList:', updatedList);
 *             console.log('optionList.onSelected.selectedList:', selectedList);
 *         }}
 *  />
 */
const AutoCompleteView = React.memo(({
                                         wrapperClassName = '',
                                         nameClassName = '',
                                         imageClassName = '',
                                         labelClassName = '',
                                         countClassName = '',
                                         tagClassName = '',
                                         removeClassName = '',
                                         dropdownItemClassName = '',
                                         dropdownMenuClassName = '',
                                         searchClassName = '',
                                         tagBoxClassName = '',
                                         refreshClassName = '',
                                         selectAllClassName = '',

                                         msgLabelColor = 'gray',
                                         nameColor = 'black',
                                         searchColor = 'black',
                                         dropdownItemHoverBg = 'lightgray',
                                         dropdownItemHoverColor = 'black',
                                         dropdownItemSelectedBg = 'darkgray',
                                         dropdownItemSelectedColor = 'gray',
                                         tagBoxBorderColor = 'lightgray',
                                         labelColor = '',
                                         countColor = 'darkgray',
                                         tagBg = '#e5e5e5',
                                         scrollVariant = "dark",
                                         listMsg = "",
                                         dropdownHeight = 300,
                                         searchPlaceholder = "Search tags...",
                                         label = "Autocomplete view",
                                         required,
                                         showLoading = false,
                                         showCount = true,
                                         showRefresh = false,
                                         showSelectAll = true,
                                         refreshLoading,
                                         disableRefresh,

                                         itemNameKey = 'name',
                                         itemImageKey = 'image',
                                         itemHoverAltNameKey = 'name',
                                         itemRemovableKey = 'removable',

                                         imageSize = '22px',
                                         showNameInitials = true,
                                         showAltNameOnHover = true,
                                         synced = false,
                                         showOptionsAfterSelection = false,
                                         showSelectedItems = false,
                                         removableTag = true,
                                         removeColor = 'black',
                                         removeSize = '20px',
                                         controlSize = '18px',
                                         tagBoxMinHeight = '',
                                         tagBoxHeight = '50vh',

                                         options = [],
                                         onSelected,
                                         onRefresh,

                                         wrapperStyle = {},
                                         nameStyle = {},
                                         imageStyle = {},
                                         tagStyle = {},
                                         tagHoverStyle = {},
                                         tagPressStyle = {},
                                         dropdownMenuStyle = {},
                                         refreshStyle = {},
                                         dropdownStyle = {},
                                         dropdownItemStyle = {},
                                         dropdownItemSelectedStyle = {},
                                         dropdownItemHoverStyle = {},
                                         dropdownItemPressStyle = {},
                                         removeStyle = {},
                                         labelStyle = {},
                                         countStyle = {},
                                         searchStyle = {},
                                         selectAllStyle = {},

                                         headerComponentEnd,
                                     }) => {
    const TAG = 'AutoCompleteView';
    const M_ID = 'mId';
    const M_ITEM_ID = 'LKJHGFDERTYUIOKJBVC';

    const [optionList, setOptionList] = useState([]);
    const [selectedMIds, setSelectedMIds] = useState(new Set());
    const [searchInputValue, setSearchInputValue] = useState("");
    const [isSelectAllActive, setIsSelectAllActive] = useState(false);

    const [itemHoverData, setItemHoverData] = useState({hover: false, name: null});
    const [itemPressedData, setItemPressedData] = useState({hover: false, name: null});

    const [dataLoadingPending, startDataLoadingTransition] = useTransition();
    const [searchPending, startSearchTransition] = useTransition();
    const [itemSelectPending, startItemSelectTransition] = useTransition();
    const [currMenuItemFocusIndex, setCurrMenuItemFocusIndex] = useState(0);

    const [scrollableParents, setScrollableParents] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const inputRef = useRef(null);
    const menuContainerRef = useRef(null);
    const tagScrollerRef = useRef(null);

    useEffect(() => {
        // log('useEffect.options:', options);
        if (isArr(options) && (isBoolTrue(synced) ? isArr(options) : !isBoolTrue(synced) && !checkNullArr(optionList))) {
            startDataLoadingTransition(() => {
                if (checkNullArr(options)) {
                    const selectedIds = new Set([]);
                    const updatedList = options.map((item, index) => {
                        if (isJsonValueTrue(item, 'selected')) {
                            selectedIds.add(index);
                        }
                        return {
                            ...item,
                            [M_ID]: index,
                        };
                    });
                    setOptionList(updatedList);
                    setSelectedMIds(selectedIds);
                }
            });
        }
    }, [options, synced]);
    useEffect(() => {
        if (anchorEl) {
            focusSearchInput();
        }
    }, [anchorEl]);

    const menuRef = useRef(null);
    useEffect(() => {
        const fun = 'focusMenuItemOnSelect:';
        try {
            // Scroll to focused item when focus index changes
            const menuList = menuRef.current;
            const focusedItem = menuList.querySelector(`[data-index="${currMenuItemFocusIndex}"]`);
            if (focusedItem) {
                const itemTop = focusedItem.offsetTop;
                const itemHeight = focusedItem.offsetHeight;
                const menuTop = menuList.scrollTop;
                const menuHeight = menuList.clientHeight;

                if (itemTop < menuTop) {
                    menuList.scrollTop = itemTop;
                } else if (itemTop + itemHeight > menuTop + menuHeight) {
                    menuList.scrollTop = (itemTop + itemHeight - menuHeight) + 50;
                }
            }
        } catch (error) {
            logErr(fun, error);
        }
    }, [currMenuItemFocusIndex]);

    const handleSearchChange = useCallback((e) => {
        const inputValue = e.target.value + '';
        setSearchInputValue(inputValue);
        focusSearchInput();
        startSearchTransition(() => {
            focusSearchInput();
            const value = e.target.value + '';
            setSearchInputValue(value);
            setOptionList((prevUserList) => prevUserList.map((item) => ({
                ...item,
                searched: (item[itemNameKey] + '').toLowerCase().includes(value.toLowerCase())
            })));
            focusSearchInput();
        });
        focusSearchInput();
    }, [startSearchTransition]);

    return (<>
        <div className={`${wrapperClassName}`} style={wrapperStyle}>
            {/*header*/}
            <Flexbox className={''} justifyAt={'between'} alignAt={'center'}>
                {/*label*/}
                {checkNullStr(label) ?
                    <Label className={`text-input-label px-2 m-0 p-0 pb-1 ${labelClassName}`} required={required}
                           color={labelColor} style={labelStyle}>{label}
                    </Label>
                    : <span></span>}

                {/*right*/}
                <Flexbox className={''} justifyAt={'end'} alignAt={'center'} position={'relative'}>
                    {/*loading*/}
                    {(!isBoolTrue(refreshLoading) && (dataLoadingPending || itemSelectPending || searchPending || isBoolTrue(showLoading)))
                        && <CircleLoading wrapperClassName={'p-3x m-0 p-0'} wrapperHeight={''}
                                          borderWidth={'2px'} size={'14px'}/>}

                    {/*count-label*/}
                    {isBoolTrue(showCount) &&
                        <Label className={`m-0 p-0 px-2 text-input-label ${countClassName}`} style={countStyle} color={countColor}>
                            {`${getArrLen(getSelectedList(), 0)}/${getArrLen(options, 0)}`}
                        </Label>}

                    {/*select-all*/}
                    {isBoolTrue(showSelectAll) && <Button
                        className={`p-1 fs-normal mx-1 border-none bg-t ${isAllListSelected() ? `text-primary highlight-primary-hover` : `highlight-dark-hover`} ${selectAllClassName}`}
                        variant={''}
                        rippleColor={RippleVarVariant.primary}
                        disableVariant={''}
                        iconClass={isAllListSelected() ? 'bi bi-check2-all' : 'bi bi-check2'}
                        tooltip={isAllListSelected() ? "Deselect All" : "Select All"}
                        disabled={!checkNullArr(optionList)}
                        width={controlSize}
                        height={controlSize}
                        color={''}
                        style={selectAllStyle}
                        onClick={() => handleSelectItem({}, true)}
                    />}

                    {/*refresh-list btn*/}
                    {isBoolTrue(showRefresh) && <div className={`${isBoolTrue(disableRefresh) ? 'disable-itemx' : ''}`}>
                        <Button
                            className={`p-0 text-nowrap fw-semi-bold m-1 ms-2 border-none text-primary bg-t highlight-primary-hover ${refreshClassName} ${isBoolTrue(disableRefresh) ? `disable-itemx` : ``}`}
                            variant={''}
                            circular={true}
                            rippleColor={RippleVarVariant.primary}
                            disableVariant={'xs'}
                            iconClass={"bi bi-arrow-clockwise"}
                            tooltip={"Refresh"}
                            width={controlSize}
                            height={controlSize}
                            color={''}
                            textOnLoading={false}
                            loadingWidth={'auto'}
                            style={refreshStyle}
                            loadingColorVariant={CssVariant.primary}
                            asLoading={isBoolTrue(refreshLoading)}
                            disabled={isBoolTrue(disableRefresh)}
                            onClick={onRefresh}
                        />
                    </div>}

                    {headerComponentEnd && headerComponentEnd}
                </Flexbox>
            </Flexbox>

            {/*tag-box*/}
            <div className={`w-100 rounded-3 border-1 scroller scroller-y-auto scroll-${scrollVariant} ${!checkNullArr(getSelectedList()) ? 'cursor-text' : ''}`}
                 style={{
                     maxHeight: tagBoxHeight,
                     minHeight: tagBoxMinHeight,
                     borderColor: tagBoxBorderColor,
                 }}
                 onClick={(e) => {
                     if (!checkNullArr(getSelectedList())) {
                         focusSearchInput(e);
                     }
                 }}
            >
                <Flexbox
                    className={`w-100 ${tagBoxClassName} ${!checkNullArr(getSelectedList()) ? 'cursor-text' : ''}`}
                    justifyAt={'start'} alignAt={'start'} wrap={'wrap'} overflow={'hidden'}
                    // borderVariant={1} borderColor={tagBoxBorderColor}
                    // style={{maxHeight: tagBoxHeight, minHeight: tagBoxMinHeight}}
                    ref={tagScrollerRef}
                    onClick={(e) => {
                        if (!checkNullArr(getSelectedList())) {
                            focusSearchInput(e);
                        }
                    }}
                >
                    {/*tags*/}
                    {getSelectedList().map((item, index) => {
                        return (<div className={"m-1"} key={index + item[itemNameKey]}>
                            <Flexbox
                                className={`m-0 rounded-10 px-1 pe-2x py-1 cursor-pointerx ${tagClassName}`}
                                bg={tagBg} style={{...tagStyle}} justifyAt={'start'} alignAt={'center'}
                                borderVariant={''}>

                                {/*tag*/}
                                <Tooltip title={isBoolTrue(showAltNameOnHover) && checkNullStr(itemHoverAltNameKey) ? item[itemHoverAltNameKey] : ''}>
                                    <div className={'m-0 p-0'}>
                                        <Flexbox className={`m-0 p-0`} justifyAt={'start'} alignAt={'center'}
                                                 onContextMenu={(e) => {
                                                     if (isBoolTrue(showAltNameOnHover) && checkNullStr(itemHoverAltNameKey)) e.preventDefault();
                                                 }}>
                                            <div className={'m-0x p-0'} style={{marginBottom: '-.21rem', marginLeft: '.1em'}}>
                                                <Avatar
                                                    wrapperClassName={'m-0 p-0'}
                                                    imgClassName={imageClassName}
                                                    alignAt={'center'}
                                                    justifyAt={'center'}
                                                    bordered={false}
                                                    showInitials={isBoolTrue(showNameInitials)}
                                                    nameForInitials={item[itemNameKey]}
                                                    imgStyle={imageStyle}
                                                    size={imageSize}>
                                                    {item[itemImageKey]}
                                                </Avatar>
                                            </div>
                                            <div className={'m-0x p-0'} style={{marginTop: '-.11rem'}}>
                                                &nbsp;
                                                <Label className={`m-0 p-0 select-text ${nameClassName}`}
                                                       color={nameColor} textAt={'start'} style={nameStyle}>
                                                    {item[itemNameKey]}
                                                </Label>
                                            </div>
                                        </Flexbox>
                                    </div>
                                </Tooltip>

                                {/*remove-btn*/}
                                {(isBoolTrue(removableTag) && isBoolTrue(getJsonValue(item, itemRemovableKey, true))) &&
                                    <Button
                                        className={`ms-1 mb-n1x shadow-none p-1 circular d-flex text-center-force ${removeClassName} `}
                                        iconClass={`bi bi-x`}
                                        width={removeSize}
                                        height={removeSize}
                                        fontSize={subtractSize(removeSize, 5)}
                                        color={removeColor}
                                        style={removeStyle}
                                        onClick={() => handleRemoveItem(item)}
                                    />}
                            </Flexbox>
                        </div>);
                    })}

                    {/* input-box */}
                    <div
                        className={`w-${checkNullArr(getSelectedList()) ? 'auto' : '100'} bg-infox d-flex position-relative m-0 p-0`}
                        ref={menuContainerRef}>
                        {/* input */}
                        <input
                            className={`w-100 border-none outline-none p-1 m-1 ${searchClassName}`}
                            ref={inputRef}
                            value={searchInputValue}
                            onChange={handleSearchChange}
                            placeholder={searchPlaceholder}
                            onKeyDown={handleSearchInputKeyDown}
                            style={{
                                color: searchColor,
                                ...searchStyle
                            }}
                            onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                focusSearchInput();
                            }}
                        />

                        {/*/!* loading *!/*/}
                        {/*{(dataLoadingPending || itemSelectPending || searchPending || isBoolTrue(showLoading))*/}
                        {/*    && <CircleLoading wrapperClassName={'p-3x m-0 p-0'} wrapperHeight={''}*/}
                        {/*                     borderWidth={'3px'} size={'20px'}/>}*/}
                    </div>
                </Flexbox>
            </div>
        </div>
        {createOptionMenu()}
    </>);

    function createOptionMenu() {
        return <Menu
            className={dropdownMenuClassName}
            anchorEl={anchorEl}
            open={openMenu}
            onOpen={(e) => {
                disableScrollingParents(e, true);
                focusSearchInput();
                handleMenuOpen(e)
                focusSearchInput();
            }}
            onClose={(e) => {
                setAnchorEl(null);
                handleMenuClose(e);
                disableScrollingParents(e, false);
            }}
            disableAutoFocusItem={true}
            disableAutoFocus={true}
            autoFocus={false}
            // onKeyDown={handleMenuItemKeyDown}
            PaperProps={{
                ref: menuRef,
                style: {
                    boxShadow: "var(--shadow)",
                    width: 'auto',
                    padding: '.8rem .6rem',
                    marginTop: '.6rem',
                    ...dropdownStyle,
                    borderRadius: ".6rem",
                    maxHeight: dropdownHeight
                }
            }}
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: "var(--bodyColor)",
                },
            }}>

            {checkNullArr(getDropdownListToRender())
                ? getDropdownListToRender().map((item, index) => {
                    return createMenuItem(item, index)
                })
                : <NullMsgLabel className={'select-none'} textAt={'start'} color={msgLabelColor}>
                    {isSearchOngoing() ? 'Search not found!'
                        : (getArrLen(getSelectedList(), 0) == getArrLen(options, 0)
                            ? (!checkNullArr(options) ? "No item available!" : 'No more items available!') : 'No items found!')}
                </NullMsgLabel>}
        </Menu>
    }

    function createMenuItem(item, index) {
        const name = item[itemNameKey];
        const highlightable = true;
        const selected = selectedMIds.has(getJsonValue(item, M_ID)) || index == currMenuItemFocusIndex;
        const focusSelected = index == currMenuItemFocusIndex;
        const hover = itemHoverData.name == name && itemHoverData.hover;
        const pressed = itemPressedData.name == name && itemPressedData.hover;

        const itemStyles = {
            margin: (isBoolTrue(showSelectedItems) && selected) ? '0 0 0.2rem 0' : '0',
            color: selected ? dropdownItemSelectedColor : nameColor,
            backgroundColor: selected ? dropdownItemSelectedBg : 'transparent',
            ...dropdownItemStyle,
            ...{
                ...hover ? {
                    color: dropdownItemHoverColor,
                    backgroundColor: dropdownItemHoverBg,
                    ...dropdownItemHoverStyle
                } : {},
                ...pressed ? dropdownItemPressStyle : {},
            },
            ...selected ? dropdownItemSelectedStyle : {},
        };

        return <MenuItem
            className={`m-0x p-0 menu-item ${dropdownItemClassName} transition-none`}
            key={index + 'nintendo' + getJsonValue(item, M_ID)}
            selected={highlightable ? selected || focusSelected : null}
            onClick={() => {
                handleSelectItem(item);
                focusSearchInput();
            }}
            // onKeydown={(e) => {
            //     e.preventDefault();
            //     log('MenuItem.onKeydown.e:', e, 'e.key:', e.key);
            // }}
            id={M_ITEM_ID + index}
            data-index={index}
            style={itemStyles}
            onFocus={() => setItemHoverData({hover: true, name: name})}
            onMouseEnter={() => setItemHoverData({hover: true, name: name})}
            onMouseLeave={() => setItemHoverData({hover: false, name: null})}
            onMouseDown={() => setItemPressedData({hover: false, name: null})}
            onMouseUp={() => setItemPressedData({hover: false, name: null})}
            sx={itemStyles}
        >
            <span
                className={'px-2 py-1 d-flex al-center jc-start'}>
                <span className={''} style={{paddingTop: '.15rem'}}>
                    <Avatar
                        wrapperClassName={''}
                        bordered={false}
                        showInitials={isBoolTrue(showNameInitials)}
                        nameForInitials={item[itemNameKey]}
                        size={multSize(imageSize, 1.3)}>{item[itemImageKey]}</Avatar>
                </span>
                <span>
                    &nbsp;&nbsp;
                    <span className={'m-0 p-0 cursor-pointer text-start'}>
                        {highlightSearchMatchedText(item[itemNameKey])}
                    </span>
                </span>
            </span>
        </MenuItem>
    }


    function handleSelectItem(item, fromSelectAll = false) {
        const fun = 'handleSelectItem:';
        const allItemsSelected = isAllListSelected();
        startItemSelectTransition(() => {
            // const updatedIds = new Set([...selectedMIds, getJsonValue(item, M_ID)]);
            let updatedIds = new Set([...selectedMIds]);
            if (checkNullJson(item)) updatedIds.add(getJsonValue(item, M_ID));

            const updatedList = optionList.map((thisItem) => {
                return {
                    ...thisItem,
                    // selected: getJsonValue(thisItem, M_ID) == getJsonValue(item, M_ID) ? true : isJsonValueTrue(thisItem, 'selected')
                    selected: isBoolTrue(fromSelectAll) ?
                        !allItemsSelected
                        : (getJsonValue(thisItem, M_ID) == getJsonValue(item, M_ID) ? true : isJsonValueTrue(thisItem, 'selected'))
                }
            });
            if (fromSelectAll) {
                if (!allItemsSelected) {
                    for (const item of updatedList) {
                        updatedIds.add(getJsonValue(item, M_ID));
                    }
                } else {
                    updatedIds = new Set([]);
                }
            }

            setOptionList(updatedList);
            setSelectedMIds(updatedIds);
            setSearchInputValue("");
            if (fromSelectAll) setIsSelectAllActive(!allItemsSelected);
            if (!isBoolTrue(showOptionsAfterSelection)) {
                setAnchorEl(null);
                focusSearchInput();
            } else {
                focusSearchInput();
            }
            focusSearchInput();
// log(fun, 'item:', item, 'updatedIds:', updatedIds, '\nupdatedList:', updatedList);
            if (onSelected) {
                onSelected(updatedList, updatedList.filter(thisItem => isJsonValueTrue(thisItem, 'selected')));
            }
            setCurrMenuItemFocusIndex(-1);
            focusSearchInput();
        });
    }

    function handleRemoveItem(item) {
        const fun = 'handleRemoveItem:';
        startItemSelectTransition(() => {
            const updatedList = optionList.map((thisItem) => {
                return {
                    ...thisItem,
                    selected: getJsonValue(thisItem, M_ID) == getJsonValue(item, M_ID) ? false : isJsonValueTrue(thisItem, 'selected')
                }
            });
            setOptionList(updatedList);

            const updatedIds = new Set(selectedMIds);
            updatedIds.delete(getJsonValue(item, M_ID));
            setSelectedMIds(updatedIds);
            // log(fun, 'item:', item, '\nupdatedIds:', updatedIds, '\nupdatedList:', updatedList);
            if (onSelected) {
                onSelected(updatedList, updatedList.filter(thisItem => isJsonValueTrue(thisItem, 'selected')));
            }
            // focusSearchInput();
        });
    }

    function handleSearchInputKeyDown(e, setAnchor = true) {
        const fun = 'handleSearchInputKeyDown:';

        disableScrollingParents(e, true);
        if (checkNullArr(optionList)) {
            if (e.key === "Backspace" && e.target.value === "") {
                if (checkNullArr(getSelectedList())) {
                    setAnchorEl(null);
                    const lastItem = getSelectedList()[getArrLen(getSelectedList()) - 1];
                    handleRemoveItem(lastItem);
                }
            } else if (e.key === 'Enter') {
                // log(fun, 'Enter.currMenuItemFocusIndex:', currMenuItemFocusIndex);
                handleSelectItem(getArrIndexValue(getDropdownListToRender(), currMenuItemFocusIndex, {}));
            } else {
                setAnchorEl(e.currentTarget);
                handleMenuItemKeyDown(e);
            }
        } else {
            setAnchorEl(e.currentTarget);
            focusSearchInput();
        }
        // if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        //     e.preventDefault();
        // }
    }

    function disableScrollingParents(e, disable = true) {
        // const fun = 'disableScrollingParents:';
        // try {
        //     if (isBoolTrue(disable)) {
        //         setAnchorEl(e.currentTarget);
        //         // if (!checkNullArr(scrollableParents)) {
        //         // log(fun, 'e.currentTarget:', e.currentTarget);
        //         const scrollingParents = checkNullArr(scrollableParents) ? scrollableParents : findScrollableParents(e.currentTarget);
        //         scrollingParents.forEach(parent => parent.classList.add('no-scroll-y-acv'));
        //         // log(fun, 'scrollingParents:', scrollingParents);
        //         if (!checkNullArr(scrollableParents)) setScrollableParents(scrollingParents);
        //         // document.body.classList.remove('no-scroll-y-acv');
        //         // }
        //     } else {
        //         // log('onClose triggered!');
        //         scrollableParents.forEach(parent => {
        //             // parent.classList.remove('no-scroll-y-acv', 'overflow-x', 'overflow-y', 'overflow-auto', 'scroller', 'scroller-y', 'scroller-y-auto', 'scroller-auto',);
        //             parent.classList.remove('no-scroll-y-acv');
        //         });
        //         setScrollableParents([]);
        //         // document.body.classList.add('no-scroll-y-acv');
        //     }
        // } catch (error) {
        //     logErr(fun, error);
        // }
    }

    function handleMenuItemKeyDown(event) {
        const fun = 'handleMenuItemKeyDown:';
        // log(fun, 'currMenuItemFocusIndex:', currMenuItemFocusIndex);
        try {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                const focusIndex = (currMenuItemFocusIndex + 1) % getArrLen(getDropdownListToRender(), 0);
                // log(fun, 'ArrowDown.focusIndex:', focusIndex);
                setCurrMenuItemFocusIndex(focusIndex);
                // focusElement(M_ITEM_ID + focusIndex, 'id');
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                const focusIndex = (currMenuItemFocusIndex === 0) ? getArrLen(getDropdownListToRender(), 0) - 1 : (currMenuItemFocusIndex <= 0 ? getArrLen(getDropdownListToRender(), 0) - 1 : currMenuItemFocusIndex - 1);
                // log(fun, 'ArrowUp.focusIndex:', focusIndex);
                setCurrMenuItemFocusIndex(focusIndex);
                // focusElement(M_ITEM_ID + focusIndex, 'id');
            }
        } catch (error) {
            logErr(fun, error);
        }
        focusSearchInput();
    }

    function handleMenuOpen() {
        if (inputRef.current) {
            focusSearchInput();
            // Add event listener to trap focus within the menu (if needed)
            document.addEventListener('focusin', handleFocusTrap, true); // Capture phase
        }
        setCurrMenuItemFocusIndex(0);
    }

    function handleFocusTrap(event) {
        if (!menuContainerRef.current.contains(event.target)) {
            focusSearchInput(); // Refocus the input if focus leaves menu
        }
    }

    function handleMenuClose() {
        setAnchorEl(null);
        // Remove focus trap event listener
        document.removeEventListener('focusin', handleFocusTrap, true);
        setCurrMenuItemFocusIndex(-1);
    }


    function focusSearchInput(e, showDropdown = false) {
        try {
            inputRef.current.focus();
            scrollToElementInScroller(tagScrollerRef.current);
            // if (e && showDropdown) {
            //     setAnchorEl(e.currentTarget);
            // }
        } catch (error) {
            logErr(error);
        }
    }

    function getSelectedList() {
        if (checkNullArr(optionList)) {
            return optionList.filter(item => isJsonValueTrue(item, 'selected'));
        }
        return [];
    }

    function getDropdownListToRender() {
        if (checkNullArr(optionList)) {
            if (isSearchOngoing()) {
                // return optionList.filter(item => isJsonValueTrue(item, 'searched') && !selectedMIds.has(getJsonValue(item,M_ID)));
                return optionList.filter(item => isJsonValueTrue(item, 'searched') && (isBoolTrue(showSelectedItems) ? true : !selectedMIds.has(getJsonValue(item, M_ID))));
            } else {
                // return optionList.filter(item => !selectedMIds.has(getJsonValue(item,M_ID)));
                return isBoolTrue(showSelectedItems) ? optionList : optionList.filter(item => !selectedMIds.has(getJsonValue(item, M_ID)));
            }
        }
        return [];
    }

    function isAllListSelected() {
        return getArrLen(getSelectedList()) == getArrLen(optionList);
    }

    function isSearchOngoing() {
        return checkNullStr(searchInputValue)
    }

    function highlightSearchMatchedText(text) {
        return isSearchOngoing() ? getHighlightedMatchedText(text, searchInputValue) : text;
    }

    /**
     * Utility function to find scrollable parents
     */
    function findScrollableParents(element) {
        const scrollableParents = [];
        try {
            while (element) {
                const style = getComputedStyle(element);
                const overflowY = style['overflowY'];
                if (overflowY === 'scroll' || overflowY === 'scrollY' || overflowY === 'scroll-y' || overflowY === 'auto') {
                    scrollableParents.push(element);
                }
                element = element.parentElement;
            }
        } catch (error) {
            logErr(error);
        }
        return scrollableParents;
    }

    function log(...text) {

        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});


export default AutoCompleteView;
